import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fetchApprovalConfig } from '@/shared/api/approval.config.api';
import { toApiError } from '@/shared/api/errors';
import { EventConfigError, toRuntimeEventConfig, type RuntimeEventConfig } from '@/shared/config/approval.mapping';
import { env } from '@/shared/config/env';
import { EVENT } from '@/shared/config/event.config';
import { EventConfigContext } from '@/shared/config/event-config-context';
import { applyThemeColor } from '@/shared/config/theme';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Spinner } from '@/shared/ui/Spinner';

interface EventConfigProviderProps {
  children: ReactNode;
}

interface LoadState {
  config: RuntimeEventConfig | null;
  error: string | null;
}

/**
 * Carrega a parametrização do evento antes de montar a tela.
 *
 * Por que bloqueia: colunas, abas e trilhas do painel saem daqui. Renderizar
 * antes significaria montar um grid sem colunas e abas sem rótulo, e depois
 * remontar tudo — pior que um spinner curto.
 *
 * Falha vira tela de erro com "tentar novamente", e não painel vazio: sem
 * parametrização não há o que listar, e o operador precisa saber que o problema é
 * a configuração do evento, não a ausência de inscrições.
 */
export function EventConfigProvider({ children }: EventConfigProviderProps) {
  const [{ config, error }, setState] = useState<LoadState>({ config: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    fetchApprovalConfig(env.miceEventId, controller.signal)
      .then(document => {
        if (controller.signal.aborted) return;
        // Antes de montar a tela: a paleta inteira sai desta cor, e aplicá-la
        // depois faria o painel piscar do azul padrão para a cor do evento.
        applyThemeColor(document.themeColor);
        // O título do documento local é só reserva, para documento gravado sem título.
        setState({ config: toRuntimeEventConfig(document, { fallbackTitle: EVENT.fullTitle }), error: null });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // Documento incompleto traz a própria mensagem, que nomeia o que falta;
        // falha de rede/HTTP passa pelo classificador comum.
        const message = cause instanceof EventConfigError ? cause.message : toApiError(cause).message;
        setState({ config: null, error: message });
      });

    return () => controller.abort();
  }, [reloadToken]);

  const retry = useCallback(() => {
    setState({ config: null, error: null });
    setReloadToken(token => token + 1);
  }, []);

  if (error) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <ErrorState title="Não foi possível carregar a parametrização do evento" message={error} onRetry={retry} />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <Spinner className="size-6 text-brand-500" label="Carregando a parametrização do evento" />
      </div>
    );
  }

  return <EventConfigContext.Provider value={config}>{children}</EventConfigContext.Provider>;
}
